import React, { useEffect, useMemo, useState } from 'react';
import cls from 'classnames';
import RegionTypeSelector, {
  RegionType,
} from '@intelligence/components/RegionCountrySelector/components/RegionTypeSelector';
import SingleCountrySelect from '@intelligence/components/ComposedCountrySelect/components/SingleCountrySelect';
import MultipleCountrySelect from '@intelligence/components/ComposedCountrySelect/components/MultipleCountrySelect';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { analysisRegionsCountriesData } from '@intelligence/components/ComposedCountrySelect/utils';
import useRenderOptionName from '@intelligence/components/RegionCountrySelector/hooks/useRenderOptionName';
import { REGION_OPTIONS_CONFIG } from '@intelligence/components/RegionCountrySelector/config';

interface IRegionCountrySelectorProps {
  onConfirm?: (value: any) => void;
  defaultValue?: { type?: string; value?: any };
  countryOptions?: any[];
  style?: React.CSSProperties;
  selectType?: string;
  value?: any;
  isAll?: any;
  onChange?: any;
  field?: any;
}

export const US_COUNTRY_VALUE = 'us';
export const US_COUNTRY_VALUE_LIST = ['us'];
const GLOBAL_REGION_VALUE = 'global';
const GLOBAL_REGION_VALUE_LIST = ['global'];

function RegionCountrySelector(props: IRegionCountrySelectorProps) {
  const {
    onConfirm,
    countryOptions,
    defaultValue = {
      type: RegionType.region,
      value: GLOBAL_REGION_VALUE,
    },
    style,
    selectType = 'single',
    // value: outValue,
    isAll,
    onChange,
    field,
  } = props;

  // const defaultValueTemp = defaultValue || value;
  // console.log('defaultValueTempdefaultValueTemp', defaultValueTemp);
  const { locale, isEN, formatMessage } = useIntlExtend();

  // 当前地区类型
  const [regionType, setRegionType] = useState(defaultValue?.type || RegionType.region);
  // 当前具体国家地区值
  const [regionCountryValue, setRegionCountryValue] = useState(
    defaultValue?.value || GLOBAL_REGION_VALUE,
  );

  // 当前具体国家地区值-list
  const [regionCountryListValue, setRegionCountryListValue] = useState(
    defaultValue?.value || GLOBAL_REGION_VALUE_LIST,
  );
  // 当前具体国家值-list
  // const [countryListValue, setCountryListValue] = useState(
  //   defaultValueTemp?.value || GLOBAL_REGION_VALUE_LIST,
  // );
  // // 当前具体地区值-list
  // const [regionListValue, setRegionListValue] = useState(
  //   defaultValueTemp?.value || US_COUNTRY_VALUE_LIST,
  // );

  // 搜索文本
  const [searchKeywords, setSearchKeywords] = useState('');

  // 将国家配置处理为 select 醉驾案 groups 和 options 格式
  const { groups, options: formattedCountryOptions = [] } = analysisRegionsCountriesData(
    countryOptions,
    locale,
  );

  // 地区配置中英文处理
  const regionOptions = useMemo(() => {
    return REGION_OPTIONS_CONFIG?.map((item) => {
      return {
        ...item,
        text: isEN ? item.text_en : item.text_cn,
        label: isEN ? item.text_en : item.text_cn,
      };
    });
  }, [isEN]);

  // 地区 options 处理
  const regionOptionsPlus = useRenderOptionName({
    options: regionOptions,
    searchKeywords,
    showCountryFlag: false,
  });

  // 国家 options 处理
  const countryOptionsPlus = useRenderOptionName({
    options: formattedCountryOptions,
    searchKeywords,
  });

  // 根据筛选器 region 类型指定选中值
  const changeValueByRegionType: (type: string) => string = (type: string) => {
    if (type === RegionType.region) {
      // 如果原有值，在当前选项列表中，使用原有值
      const isValidValue = regionOptions?.find((item) => item.value === regionCountryValue);
      if (isValidValue) return regionCountryValue;

      // 无可用原有值，使用列表内第一项的值作为默认值
      const firstRegionValue = regionOptions?.[0]?.value;
      setRegionCountryValue(firstRegionValue);
      if (selectType === 'multiple') {
        setRegionCountryListValue([firstRegionValue]);
      }
      return firstRegionValue;
    }

    if (type === RegionType.country) {
      // 如果原有值，在选项列表中，使用原有值
      // const isValidValue = formattedCountryOptions?.find(
      //   (item) => item.value === regionCountryValue,
      // );
      // if (isValidValue) return regionCountryValue;

      // 如果列表中有美国，将美国作为默认选项传出
      const ifHasUS = formattedCountryOptions?.find((item) => item?.value === US_COUNTRY_VALUE);
      if (ifHasUS) {
        setRegionCountryValue(US_COUNTRY_VALUE);
        if (selectType === 'multiple') {
          setRegionCountryListValue([US_COUNTRY_VALUE]);
        }
        return US_COUNTRY_VALUE;
      }

      // 无默认值，使用列表内第一项的值作为默认值

      const firstCountryValue = formattedCountryOptions?.[0]?.value;
      setRegionCountryValue(firstCountryValue);
      if (selectType === 'multiple') {
        setRegionCountryListValue([firstCountryValue]);
      }
      return firstCountryValue;
    }

    return GLOBAL_REGION_VALUE;
  };

  const handleTypeChange = (type: string) => {
    // 更新当前选中的 region 类型
    setRegionType(type);

    // 更新并获取当前选中类型对应选中的具体国家或地区值
    const value = changeValueByRegionType(type);

    // 如果是多选组件
    if (selectType === 'multiple') {
      onChange?.(field, { type, value });
    }

    // onConfirm 调用传入当前最新值
    onConfirm?.({ type, value });
  };

  // 切换具体国家或地区
  const handleRegionCountrySelectChange = (value: string) => {
    setRegionCountryValue(value);
    onConfirm?.({ type: regionType, value });
  };

  const handleRegionCountryListSelectChange = (value: string) => {
    setRegionCountryListValue(value);
    // 如果是多选组件
    onChange?.(field, { type: regionType, value });
  };

  // 搜索
  const handleSearch = (payload?: string) => {
    const keywordsTmp = payload ?? '';
    setSearchKeywords(keywordsTmp);
  };

  // 关闭时清空搜索值
  const handleClose = () => {
    setSearchKeywords('');
  };

  // 国家选项变化后，判断处理当前国家值是否在选项中
  useEffect(() => {
    if (regionType !== RegionType.country) return;

    // 如果原有值，在选项列表中，使用原有值
    const isValidValue = formattedCountryOptions?.find((item) => item.value === regionCountryValue);
    if (isValidValue) return;

    // 如果列表中有美国，将美国作为默认选项传出
    const ifHasUS = formattedCountryOptions?.find((item) => item?.value === US_COUNTRY_VALUE);
    if (ifHasUS) {
      setRegionCountryValue(US_COUNTRY_VALUE);
      return;
    }

    // 无默认值，使用列表内第一项的值作为默认值
    const firstCountryValue = formattedCountryOptions?.[0]?.value;
    setRegionCountryValue(firstCountryValue);
  }, [formattedCountryOptions, regionCountryValue, regionType]);

  // 初次默认赋值
  useEffect(() => {
    // 如果是多选组件
    if (selectType === 'multiple') {
      onChange?.(field, {
        type: defaultValue?.type || RegionType.region,
        value: defaultValue?.value || GLOBAL_REGION_VALUE_LIST,
      });
    }
  }, []);

  return (
    <div className="dbi_region_country_selector" style={style}>
      <RegionTypeSelector
        onChange={handleTypeChange}
        defaultValue={defaultValue?.type || RegionType.region}
      />

      {selectType === 'single' && regionType === RegionType.region && (
        <SingleCountrySelect
          className={cls(
            // 原有组件类名
            'dbi-composed-country-select_country-select',
            'dbi-composed-country-select_single-country-select',
            // 当前样式控制类名
            'dbi-region-country-selector_country-select',
          )}
          codeSearchable={false}
          overlayClassName="dbi-region-country-selector-overlay"
          isShowButtonLabel={false}
          options={regionOptionsPlus}
          value={regionCountryValue}
          onChange={handleRegionCountrySelectChange}
          onSearch={handleSearch}
          onClose={handleClose}
        />
      )}

      {selectType === 'single' && regionType === RegionType.country && (
        <SingleCountrySelect
          className={cls(
            // 原有组件类名
            'dbi-composed-country-select_country-select',
            'dbi-composed-country-select_single-country-select',
            // 当前样式控制类名
            'dbi-region-country-selector_country-select',
          )}
          codeSearchable={false}
          overlayClassName="dbi-region-country-selector-overlay"
          isShowButtonLabel={false}
          groups={groups}
          options={countryOptionsPlus}
          value={regionCountryValue}
          onChange={handleRegionCountrySelectChange}
          onSearch={handleSearch}
          onClose={handleClose}
          tips={formatMessage({ id: 'dbi_overlap_country_selector_no_data' })}
        />
      )}

      {/* 多选 地区 */}
      {selectType === 'multiple' && regionType === RegionType.region && (
        <MultipleCountrySelect
          className="dbi-composed-country-select_country-select dbi-composed-country-select_multiple-country-select dbi-region-country-selector_country-select"
          // renderOptionName={renderOptionName}
          groups={groups}
          options={regionOptionsPlus}
          value={regionCountryListValue}
          onConfirm={handleRegionCountryListSelectChange}
          onSearch={handleSearch}
          onClose={handleClose}
          isAll={isAll}
          style={{
            width: 256
          }}
        />
      )}

      {/* 多选 国家 */}
      {selectType === 'multiple' && regionType === RegionType.country && (
        <MultipleCountrySelect
          className="dbi-composed-country-select_country-select dbi-composed-country-select_multiple-country-select dbi-region-country-selector_country-select"
          // renderOptionName={renderOptionName}
          groups={groups}
          options={countryOptionsPlus}
          value={regionCountryListValue}
          onConfirm={handleRegionCountryListSelectChange}
          onSearch={handleSearch}
          onClose={handleClose}
          isAll={isAll}
        />
      )}
    </div>
  );
}

export default RegionCountrySelector;
